import React from 'react';
import { Question } from '../../../../types/api';
import { Input } from '../../Input';
import {
  TextArea,
  RadioGroup,
  RadioOption,
  RadioInput,
  FileUploadArea,
  FileUploadIcon,
  FileUploadText,
  FileUploadSubtext,
} from '../DynamicQuestionForm.styled';
import { Tooltip } from '../../Tooltip';
import { SelectDropdown } from '../../SelectDropdown';
import { MapWithMarker } from '../CustomFields/MapWithMarker';
import RadioGrid from '../CustomFields/RadioGrid';
import { SearchableInput } from '../CustomFields/SearchableInput';
import {
  createAnswerValue,
  getFieldStyles,
  getPlaceholder,
  shouldShowUploadSectionForRadioGrid,
} from '../utils/questionFieldUtils';
import { useHotelQuestionnaireAnswers } from '../../../../hooks';
import { useSplash } from '../../../../contexts/SplashContext';
import { useHotelDetail } from '../../../../contexts/HotelDetailContext';

interface BasicFieldRendererProps {
  question: Question;
  value: any;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderUploadSection: () => React.ReactNode;
  renderApproveSection: () => React.ReactNode;
  shouldShowUploadSection: () => boolean;
  answers?: Record<string, any>;
  currentStepKey?: string;
  isInSampleQuestionnaire?: boolean;
  hasCountryChangedRef?: React.MutableRefObject<boolean>;
  hasDestinationChangedRef?: React.MutableRefObject<boolean>;
}

export const BasicFieldRenderer: React.FC<BasicFieldRendererProps> = ({
  question,
  value,
  handleFileChange,
  renderUploadSection,
  renderApproveSection,
  shouldShowUploadSection,
  answers,
  currentStepKey,
  isInSampleQuestionnaire = false,
  hasCountryChangedRef,
  hasDestinationChangedRef,
}) => {
  const { updateDraftAnswer } = useHotelQuestionnaireAnswers();
  const { requestAnswerSplash } = useSplash();
  const { isBoostHotel } = useHotelDetail();
  const hasInitializedCountryRef = React.useRef(false);

  // Initialize country field answer with country_id if defaultValue exists but no answer yet
  React.useEffect(() => {
    if (
      !hasInitializedCountryRef.current &&
      (question.id === 'hotel_location_country' || question.key === 'hotel_location_country') &&
      question.type === 'select' &&
      question.defaultValue &&
      question.options &&
      !value // No answer stored yet
    ) {
      // Find the matching option by defaultValue (which is the label)
      const matchedOption = question.options.find(
        (opt: any) =>
          opt.value === question.defaultValue ||
          opt.label === question.defaultValue
      );

      if (matchedOption && (matchedOption as any).country_id) {
        const answerValue = createAnswerValue(
          String((matchedOption as any).id || (matchedOption as any).value),
          null
        );
        (answerValue as any).country_id = (matchedOption as any).country_id;
        
        updateDraftAnswer(question.id, answerValue);
        hasInitializedCountryRef.current = true;
      }
    }
  }, [question.id, question.key, question.type, question.defaultValue, value]);

  const renderBasicField = () => {
    // Check for custom component attribute first (overrides type)
    if (question.component === 'searchable-input') {
      // Get country_id from answers first, then fallback to config
      let countryId = null;
      let destinationId = null;
      
      const countryAnswer = answers?.['hotel_location_country'];
      countryId = countryAnswer?.value?.country_id || question.config?.searchParams?.country_id || null;
      
      // For city field, also get destination_id from answers
      if (question.id === 'giata_city_id') {
        const destinationAnswer = answers?.['giata_destination_id'];
        // Only use config fallback if there's no answer at all (undefined)
        // If answer exists but selectedOptionId is null, that means it was explicitly cleared, so keep it null
        if (destinationAnswer?.value) {
          destinationId = destinationAnswer.value.selectedOptionId;
        } else {
          destinationId = question.config?.searchParams?.destination_id || null;
        }
      }
      
      const initialCountryId = question.config?.searchParams?.country_id;
      const initialDestinationId = question.config?.searchParams?.destination_id;
      
      return (
        <SearchableInput
          question={question}
          value={value}
          onChange={value => {
            if (currentStepKey === 'sources') {
              return;
            }
            updateDraftAnswer(question.id, value);
            
            // Reset city when destination changes (only if a value is actually being set, not cleared)
            if (question.id === 'giata_destination_id') {
              // Only update refs if user is actually selecting a destination (not when it's being cleared)
              if (hasDestinationChangedRef && hasCountryChangedRef && (value?.selectedOptionId || value?.typedValue)) {
                hasDestinationChangedRef.current = true;
                hasCountryChangedRef.current = false; // Reset country changed ref when destination changes
              }
              updateDraftAnswer('giata_city_id', { selectedOptionId: null, typedValue: null, label: undefined });
            }

            if (question.id === "giata_city_id" && hasDestinationChangedRef && (value?.selectedOptionId || value?.typedValue)) {
              hasDestinationChangedRef.current = false; // Reset destination changed ref when city changes
            }
          }}
          countryId={countryId}
          destinationId={destinationId}
          hasCountryChanged={hasCountryChangedRef?.current}
          hasDestinationChanged={hasDestinationChangedRef?.current}
        />
      );
    }

    switch (question.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <div className="flex flex-col gap-2">
            <Input
              type={question.type}
              value={value?.typedValue ?? question.defaultValue ?? ''}
              onChange={e => {
                if (currentStepKey === 'sources') {
                  return;
                }
                updateDraftAnswer(
                  question.id,
                  createAnswerValue(
                    null,
                    ['firstName', 'lastName'].includes(question.key as string)
                      ? e.target.value.trim()
                      : e.target.value
                  )
                );
              }}
              showValidationError={['url', 'email', 'phone', 'tel'].includes(
                question.type
              )}
              placeholder={getPlaceholder(question)}
              {...question.attributes}
              required={question.required}
              pattern={
                question.validation_rules?.pattern ??
                question.validation?.pattern
              }
              minLength={
                question.validation_rules?.min_length ||
                question.attributes?.minLength
              }
              maxLength={
                question.validation_rules?.max_length ||
                question.attributes?.maxLength
              }
              style={{
                cursor: question.attributes?.readOnly ? 'not-allowed' : 'text',
                backgroundColor: question.attributes?.readOnly
                  ? 'var(--color-gray-25)'
                  : 'white',
                '&:focus': `${
                  question.attributes?.readOnly
                    ? 'border-none ring-none'
                    : 'ring-none'
                }`,
                ...question.attributes?.style,
              }}
            />
            {shouldShowUploadSection() && renderUploadSection()}
            {renderApproveSection()}
          </div>
        );

      case 'number':
        return (
          <>
            <Input
              type="number"
              value={value?.typedValue ?? question.defaultValue ?? ''}
              onChange={e => {
                if (currentStepKey === 'sources') {
                  return;
                }
                updateDraftAnswer(
                  question.id,
                  createAnswerValue(null, e.target.value)
                );
              }}
              {...question.attributes}
              placeholder={getPlaceholder(question)}
              required={question.required}
              min={
                question.validation_rules?.min_value ?? question.validation?.min
              }
              max={
                question.validation_rules?.max_value ?? question.validation?.max
              }
              step={question.validation_rules?.allow_decimal ? 'any' : '1'}
              style={{
                cursor: question.attributes?.readOnly ? 'not-allowed' : 'text',
                ...question.attributes?.style,
              }}
            />
            {shouldShowUploadSection() && renderUploadSection()}
            {renderApproveSection()}
          </>
        );

      case 'textarea':
        return (
          <>
            <TextArea
              value={value?.typedValue ?? question.defaultValue ?? ''}
              onChange={e => {
                if (currentStepKey === 'sources') {
                  return;
                }
                updateDraftAnswer(
                  question.id,
                  createAnswerValue(null, e.target.value)
                );
              }}
              placeholder={getPlaceholder(question)}
              required={question.required}
              rows={4}
              style={{
                cursor: question.attributes?.readOnly ? 'not-allowed' : 'text',
                ...question.attributes?.style,
              }}
              {...question.attributes}
            />
            {renderApproveSection()}
          </>
        );

      case 'select':
        let selectValue;
        let optionsToRender = question.options || [];

        // If user has already selected a value, use that
        if (value?.selectedOptionId) {
          selectValue = value.selectedOptionId;
        }
        // Otherwise, handle defaultValue for initial render
        else if (question.defaultValue && question.options) {
          const matchedOption = question.options.find(
            opt =>
              opt.value === question.defaultValue ||
              opt.label === question.defaultValue
          );

          if (matchedOption) {
            selectValue = matchedOption.value || matchedOption.id;
          } else {
            // Create dummy option if defaultValue doesn't match any option
            const dummyId = `dummy-${question.id}-${Date.now()}`;
            optionsToRender = [
              ...question.options,
              { id: dummyId, value: dummyId, label: question.defaultValue },
            ];
            selectValue = dummyId;
          }
        } else {
          selectValue = question.defaultValue || '';
        }
        return (
          <SelectDropdown
            options={optionsToRender.map(option => ({
              label: option.label,
              value: String(option.id || option.value),
            }))}
            value={selectValue || ''}
            onChange={value => {
              if (currentStepKey === 'sources') {
                return;
              }
              
              // For country field, store country_id as metadata and reset dependent fields
              if (question.id === 'hotel_location_country' || question.key === 'hotel_location_country') {
                const selectedOption = optionsToRender.find(
                  opt => String(opt.id || opt.value) === value
                );
                const answerValue = createAnswerValue(value, null);
                if (hasCountryChangedRef) {
                  hasCountryChangedRef.current = true;
                }
                if (hasDestinationChangedRef) {
                  hasDestinationChangedRef.current = false; // Reset destination changed ref when country changes
                }
                // Add country_id to the answer if available
                if (selectedOption && (selectedOption as any).country_id) {
                  (answerValue as any).country_id = (selectedOption as any).country_id;
                }
                updateDraftAnswer(question.id, answerValue);
                
                // Reset destination and city when country changes - explicitly clear all properties including label
                updateDraftAnswer('giata_destination_id', { selectedOptionId: null, typedValue: null, label: undefined });
                updateDraftAnswer('giata_city_id', { selectedOptionId: null, typedValue: null, label: undefined });
              } else {
                updateDraftAnswer(question.id, createAnswerValue(value, null));
              }
            }}
            searchable
            placeholder="Select an option"
            disabled={question.attributes?.readOnly}
            className="!max-h-12"
            variant="transparent"
          />
        );

      case 'radio':
        return (
          <div className="flex flex-col gap-2">
            <RadioGroup>
              {question.options?.map(option => (
                <RadioOption key={String(option.id)}>
                  <RadioInput
                    type="radio"
                    name={question.id}
                    value={String(option.id)}
                    checked={
                      String(value?.selectedOptionId) === String(option.id)
                    }
                    disabled={question?.attributes?.readOnly}
                    onChange={e => {
                      if (currentStepKey === 'sources') {
                        return;
                      }
                      //show splash screen when user selects 'yes' for certification question
                      if (
                        !isInSampleQuestionnaire &&
                        !isBoostHotel &&
                        currentStepKey === 'certifications' &&
                        option.label.toLowerCase() === 'yes'
                      ) {
                        requestAnswerSplash();
                      }
                      const selectedValue = e.target.value;
                      updateDraftAnswer(
                        question.id,
                        createAnswerValue(selectedValue, null)
                      );

                      // Handle autofill if this question enables autofill
                      if (
                        question.enables_autofill &&
                        question.autofill_targets
                      ) {
                        const selectedOption = question.options?.find(
                          opt => String(opt.id) === selectedValue
                        );

                        // Only trigger autofill if the selected option has triggers_autofill = true
                        if (selectedOption?.triggers_autofill) {
                          // Update all target questions with the same selected option
                          question.autofill_targets.forEach(target => {
                            updateDraftAnswer(
                              target.id,
                              createAnswerValue(selectedValue, null)
                            );
                          });
                        } else {
                          //clear target questions if the selected option does not trigger autofill
                          question.autofill_targets.forEach(target => {
                            updateDraftAnswer(
                              target.id,
                              createAnswerValue(null, null)
                            );
                          });
                        }
                      }
                    }}
                    required={question.required}
                    style={getFieldStyles(question)}
                    {...question.attributes}
                  />
                  <div
                    style={getFieldStyles(question, {
                      position: 'relative',
                      opacity:
                        question.attributes?.readOnly ||
                        question.attributes?.disabled
                          ? 0.85
                          : 1,
                    })}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {option.label}
                      {option.infotext && (
                        <Tooltip
                          content={option.infotext}
                          position="top"
                          maxWidth={240}
                        />
                      )}
                    </span>
                  </div>
                </RadioOption>
              ))}
            </RadioGroup>

            {shouldShowUploadSection() && renderUploadSection()}
            {renderApproveSection()}
          </div>
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              id={question.id}
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <FileUploadArea
              onClick={() => document.getElementById(question.id)?.click()}
            >
              <FileUploadIcon>📎</FileUploadIcon>
              <FileUploadText>
                Drag and drop files here or{' '}
                <span
                  style={{
                    color: 'var(--color-primary-600)',
                    textDecoration: 'underline',
                  }}
                >
                  browse
                </span>
              </FileUploadText>
              <FileUploadSubtext>PDF, DOC, DOCX up to 10MB</FileUploadSubtext>
              {value && value.length > 0 && (
                <div style={{ marginTop: 'var(--spacing-3)' }}>
                  {value.map((fileName: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        display: 'inline-block',
                        margin: 'var(--spacing-1)',
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        backgroundColor: 'var(--color-primary-100)',
                        borderRadius: 'var(--radius-base)',
                        fontSize: 'var(--font-size-14)',
                        color: 'var(--color-primary-700)',
                      }}
                    >
                      {fileName}
                    </div>
                  ))}
                </div>
              )}
            </FileUploadArea>
          </div>
        );

      case 'custom':
        // Handle custom components based on the component type
        switch (question.component) {
          case 'map-with-marker':
            return (
              <MapWithMarker
                question={question}
                value={value}
                onChange={value => updateDraftAnswer(question.id, value)}
              />
            );
          case 'radio-grid':
            return (
              <div className="flex flex-col gap-2">
                <RadioGrid
                  question={question}
                  answers={answers}
                  onAnswerChange={(subQuestionId, value) => {
                    if (currentStepKey === 'sources') {
                      return;
                    }
                    // Handle each sub-question as an individual answer
                    updateDraftAnswer(subQuestionId, value);
                  }}
                />
                {(currentStepKey === 'sources' ||
                  shouldShowUploadSectionForRadioGrid(question, answers)) &&
                  renderUploadSection()}
                {renderApproveSection()}
              </div>
            );
          case 'searchable-input':
            // Get country_id from answers for location-dependent fields
            const countryAnswerCustom = answers?.['hotel_location_country'];
            const countryIdCustom = countryAnswerCustom?.value?.country_id || null;
            
            // For city field, also get destination_id from answers
            let destinationIdCustom = null;
            if (question.id === 'giata_city_id') {
              const destinationAnswerCustom = answers?.['giata_destination_id'];
              // Only use config fallback if there's no answer at all (undefined)
              // If answer exists but selectedOptionId is null, that means it was explicitly cleared, so keep it null
              if (destinationAnswerCustom?.value) {
                destinationIdCustom = destinationAnswerCustom.value.selectedOptionId;
              } else {
                destinationIdCustom = question.config?.searchParams?.destination_id || null;
              }
            }
            
            return (
              <SearchableInput
                question={question}
                value={value}
                onChange={value => {
                  if (currentStepKey === 'sources') {
                    return;
                  }
                  updateDraftAnswer(question.id, value);
                }}
                countryId={countryIdCustom}
                destinationId={destinationIdCustom}
              />
            );
          default:
            return (
              <div
                style={{
                  padding: 'var(--spacing-4)',
                  border: '1px dashed var(--color-gray-300)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  color: 'var(--color-gray-500)',
                }}
              >
                Custom component: {question.component}
              </div>
            );
        }

      default:
        return null;
    }
  };

  return renderBasicField();
};
